/**
 * A fixture's markets, each expandable to its outcomes — `.m-mkts-detailed`
 * and `.m-outcome*` in the prototype's modals.css.
 *
 * Built once and consumed by three screens, which is what CAE-1550 asks for:
 * Edit › Fixtures (this ticket), the Catalogue (CAE-1555) and the Add Fixtures
 * modal (CAE-1551). `03-catalogue/catalogue-app.jsx` is the prototype it is
 * taken from — the read-only form of the drill-down, with no outcome selection.
 * The Add **Outcomes** modal (CAE-1553) draws the same markup with a checkbox
 * per outcome; that is a picker, and it is not this.
 *
 * > **Which drill-down this screen gets is a place the handoff contradicts
 * > itself.** `05-edit-fixtures/fixtures-app.jsx` expands a row to a flat
 * > `.m-mkts` list with no outcomes under it, and `modals.css` labels that
 * > block "Markets list (fixtures modal - read-only)". The screen README and
 * > CAE-1550's own scope both say "markets, each itself expandable to outcomes,
 * > reusing the Catalogue's drill-down", and the ticket's notes ask for it to
 * > be shared across the three screens by name. The ticket is taken: it is the
 * > contract, it says so twice, and no screenshot shows an expanded row either
 * > way. Logged in docs/design/admin-ui/prototype/README.md.
 *
 * Nothing here is fetched until it is asked for. The markets arrive when a
 * fixture row opens, and a market's outcomes when that market opens — the
 * catalogue has no endpoint that returns outcomes for a fixture in one call,
 * and totalling them eagerly is the 282-request fan-out CAE-1573 removed from
 * the Contest Overview. The counts a screen shows *before* any of this are the
 * contest payload's, not these queries', so opening a row adds requests rather
 * than repeating any.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import type { Theme } from '@mui/material/styles';
import {
  catalogueApi,
  type CatalogueLocale,
  type CatalogueMarket,
  type CatalogueOutcome,
} from '../../api/catalogue';
import {
  AdminEmptySection,
  AdminInput,
  ChevronDownIcon,
  ChevronRightIcon,
  EditIcon,
  InfoIcon,
  OutcomeStatus,
  Popover,
  RefreshIcon,
} from '../ui';
import { summariseSkippedReasons, syncDeliveredNothing } from './sync-markets-outcome';
import { InfoPanel } from './info-panel';

/** `.m-outcomes-head` / `.m-outcome` share one grid, so the head stays over its column. */
const OUTCOME_COLUMNS = '1fr 90px 90px 110px 36px';

/** The catalogue's own 60s staleness, so a drill-down and a sync agree on what is fresh. */
const CATALOGUE_STALE_MS = 60_000;

/**
 * How tall the market list may grow before it scrolls inside itself.
 *
 * Nothing in the handoff caps it, because the prototype's mock fixture has four
 * markets. A real one has 141 (Portugal vs DR Congo, verified live), and
 * expanding it pushes every other attached fixture off the screen — the
 * operator opens one row and loses the list they opened it from. Same trade
 * CAE-1542 and CAE-1574 made for their wide tables: the content scrolls in its
 * own container rather than moving the page.
 */
const MARKET_LIST_MAX_HEIGHT = 420;

export const marketsQueryKey = (fixtureExternalId: string, locale: CatalogueLocale) =>
  ['admin', 'catalogue', 'markets', fixtureExternalId, locale] as const;

export const outcomesQueryKey = (marketExternalId: string, locale: CatalogueLocale) =>
  ['admin', 'catalogue', 'outcomes', marketExternalId, locale] as const;

/**
 * What a market is called, in the operator's order of preference.
 *
 * The override is an operator's own word for it and wins; the localised name is
 * the feed's for this locale; `name` is the feed's canonical. The type code is
 * the last resort and never blank — a row with no name at all is unusable, and
 * the code is at least something to search the feed for.
 */
const marketLabel = (market: CatalogueMarket): string =>
  market.displayNameOverride ?? market.localisedName ?? market.name ?? market.type;

/** `.m-outcome-col-odds` — an em-dash for a price the feed has not sent. */
const Price = ({ children }: { children: string | null }) => (
  <Box
    sx={(theme: Theme) => ({
      fontFamily: theme.tokens.font.mono,
      fontSize: '13px',
      fontWeight: 500,
      color: children === null ? theme.tokens.color.ink4 : theme.tokens.color.ink2,
      textAlign: 'right',
      fontVariantNumeric: 'tabular-nums',
      letterSpacing: '-0.01em',
    })}
  >
    {children ?? '—'}
  </Box>
);

const ColumnHead = ({ children, align }: { children?: string; align?: 'right' | 'center' }) => (
  <Box
    sx={(theme: Theme) => ({
      fontSize: '10.5px',
      fontWeight: 600,
      letterSpacing: '0.06em',
      textTransform: 'uppercase',
      color: theme.tokens.color.ink4,
      textAlign: align ?? 'left',
    })}
  >
    {children}
  </Box>
);

const MarketInfoButton = ({
  market,
  label,
  fixtureLabel,
  outcomeSummary,
}: {
  market: CatalogueMarket;
  label: string;
  fixtureLabel: string;
  /** Only once the outcomes are loaded — a count shown before it is true is a lie. */
  outcomeSummary?: string;
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  return (
    <>
      <IconButton
        size="small"
        aria-label={`Details for ${label}`}
        onClick={(event) => {
          event.stopPropagation();
          /*
           * Read before the updater, not inside it. React nulls
           * `currentTarget` in a `finally` once the listener returns, so an
           * updater that is evaluated later — any time React cannot take the
           * eager-state path — would anchor the panel to `null` and it would
           * silently never open.
           */
          const anchor = event.currentTarget;
          setAnchorEl((previous) => (previous ? null : anchor));
        }}
        sx={(theme: Theme) => ({
          width: '26px',
          height: '26px',
          borderRadius: `${theme.tokens.radius.idChip}px`,
          color: theme.tokens.color.ink4,
          border: `${theme.tokens.border.width} solid transparent`,
          '&:hover': {
            backgroundColor: theme.tokens.color.surface,
            color: theme.tokens.color.ink2,
            borderColor: theme.tokens.color.lineStrong,
          },
        })}
      >
        <InfoIcon sx={{ fontSize: '12px' }} />
      </IconButton>
      <Popover
        open={anchorEl !== null}
        anchorEl={anchorEl}
        label="Market details"
        onClose={() => setAnchorEl(null)}
      >
        {/*
         * Stopped here, even though the panel is portalled to `document.body`.
         * React propagates synthetic events through the *React* tree, not the
         * DOM one, so a click in the panel still reaches the market head's
         * toggle two components up — and selecting the market ID to copy it,
         * which is what this panel is for, would silently open the market and
         * fetch its outcomes.
         */}
        <Box onClick={(event) => event.stopPropagation()}>
          {/*
           * No "Open in catalogue" foot. The prototype's popover carries one and
           * takes a `hideOpenInCatalogue` prop to drop it; every screen that
           * mounts this drill-down passes that prop — Edit › Fixtures because the
           * operator is already inside the contest editor, the Catalogue because
           * it *is* the catalogue, and the Add Fixtures modal because leaving the
           * page would throw away the selection. A prop with one value at every
           * call site is a setting, not a choice, so the link is simply not here.
           */}
          <InfoPanel
            eyebrow="Market"
            title={label}
            rows={[
              { key: 'Market ID', value: market.externalId, mono: true, truncate: true },
              { key: 'Catalogue type', value: market.type, mono: true },
              { key: 'Fixture', value: fixtureLabel, truncate: true },
              {
                key: 'Fixture ref',
                value: market.fixtureExternalId,
                mono: true,
                truncate: true,
              },
              ...(outcomeSummary ? [{ key: 'Outcomes', value: outcomeSummary, mono: true }] : []),
              /*
               * No "Last synced". The prototype's popover ends on it; nothing
               * in the database records when a market was pulled — CAE-1597
               * records sync runs, not rows — and a time invented here would be
               * the same slop the header pill refuses.
               */
            ]}
          />
        </Box>
      </Popover>
    </>
  );
};

/**
 * `OutcomeInfoPopover`, restyled — the selection's own ids and its three prices.
 *
 * The old page carried one of these beside every outcome and the rebuild
 * dropped it, leaving the row's last 36px column rendering an empty `Box` where
 * it used to sit. CAE-1555 asks for all three info popovers to survive the
 * restructure, and this is the one that answers "which selection is this, and
 * what is the book actually offering on it" — the question an operator
 * reconciling against Caesars is here to ask.
 *
 * American odds live here rather than in the row. The row draws the two the
 * design asks for, decimal and fractional; the third is a fact about the
 * selection, which is what this panel is for.
 */
const OutcomeInfoButton = ({
  outcome,
  label,
  eyebrow,
  fixtureLabel,
}: {
  outcome: CatalogueOutcome;
  /** The selection's own name — the panel's title. */
  label: string;
  /** The market it belongs to, which is what the prototype's eyebrow names. */
  eyebrow: string;
  fixtureLabel: string;
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const american =
    outcome.americanPrice === null
      ? null
      : outcome.americanPrice > 0
        ? `+${outcome.americanPrice}`
        : String(outcome.americanPrice);

  return (
    <>
      <IconButton
        size="small"
        aria-label={`Details for ${label}`}
        onClick={(event) => {
          event.stopPropagation();
          // Read before the updater, for the reason `MarketInfoButton` gives.
          const anchor = event.currentTarget;
          setAnchorEl((previous) => (previous ? null : anchor));
        }}
        sx={(theme: Theme) => ({
          width: '26px',
          height: '26px',
          borderRadius: `${theme.tokens.radius.idChip}px`,
          color: theme.tokens.color.ink4,
          border: `${theme.tokens.border.width} solid transparent`,
          '&:hover': {
            backgroundColor: theme.tokens.color.surface,
            color: theme.tokens.color.ink2,
            borderColor: theme.tokens.color.lineStrong,
          },
        })}
      >
        <InfoIcon sx={{ fontSize: '12px' }} />
      </IconButton>
      <Popover
        open={anchorEl !== null}
        anchorEl={anchorEl}
        label="Outcome details"
        onClose={() => setAnchorEl(null)}
      >
        {/* Stopped for the reason the market panel stops it — the synthetic
            event would otherwise reach the market head and collapse it. */}
        <Box onClick={(event) => event.stopPropagation()}>
          <InfoPanel
            eyebrow={eyebrow}
            title={label}
            rows={[
              { key: 'Outcome ID', value: outcome.externalId, mono: true, truncate: true },
              { key: 'Market ID', value: outcome.marketExternalId, mono: true, truncate: true },
              { key: 'Fixture', value: fixtureLabel, truncate: true },
              {
                /*
                 * One row, as the prototype draws it — the two notations are
                 * the same price, and splitting them read as two facts.
                 */
                key: 'Decimal · Fractional',
                value:
                  outcome.decimalPrice === null && outcome.fractionalPrice === null
                    ? '—'
                    : `${outcome.decimalPrice === null ? '—' : outcome.decimalPrice.toFixed(2)} · ${
                        outcome.fractionalPrice ?? '—'
                      }`,
                mono: true,
              },
              /*
               * Not in the prototype's row list, but it was in the popover this
               * one replaces and the feed sends it — dropping a price the admin
               * already had would be a quiet loss.
               */
              { key: 'American', value: american ?? '—', mono: true },
              {
                key: 'Implied probability',
                value:
                  outcome.decimalPrice === null || outcome.decimalPrice <= 0
                    ? '—'
                    : `${(100 / outcome.decimalPrice).toFixed(1)}%`,
                mono: true,
              },
              {
                key: 'Status',
                value: (
                  <OutcomeStatus
                    suspended={outcome.active === null ? null : outcome.active === false}
                  />
                ),
              },
              /*
               * No "Last updated". Nothing records when an outcome was pulled —
               * CAE-1597 records sync runs, not rows — and the prototype's "2m
               * ago" is mock data, not a column.
               */
            ]}
          />
        </Box>
      </Popover>
    </>
  );
};

/**
 * `markets.display_name_override` — the operator's own word for a feed market,
 * edited in place on the market head.
 *
 * Opt-in, and only the Catalogue opts in. It is the single surface in the admin
 * that writes this column — every other reads it through `marketLabel` — so
 * dropping the control when the old page's markup went would have made the
 * column unwritable. The same argument CAE-1550 made for carrying Edit ›
 * Fixtures' rename pencil onto the new row.
 *
 * Not in the info popover beside the ids, though that is where the facts about
 * a market live: that panel is a read, and a form inside a floating layer
 * anchored to a row that closes on outside click is a worse place to type than
 * the row itself.
 */
const MarketRename = ({
  market,
  label,
  locale,
}: {
  market: CatalogueMarket;
  label: string;
  locale: CatalogueLocale;
}) => {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(market.displayNameOverride ?? '');

  const save = useMutation({
    mutationFn: () => {
      const trimmed = draft.trim();
      /*
       * Empty clears rather than stores. Clearing the box is how an operator
       * goes back to the feed's own name, and `''` would be an override that
       * renders the market blank — the service normalises it the same way, but
       * the intent belongs on the caller that has it.
       */
      return catalogueApi.updateMarketDisplayNameOverride(
        market.externalId,
        trimmed === '' ? null : trimmed,
        locale,
      );
    },
    onSuccess: async () => {
      setEditing(false);
      // The market list is what carries `displayNameOverride`, so the head
      // redraws from the server's answer rather than from the draft.
      await queryClient.invalidateQueries({
        queryKey: marketsQueryKey(market.fixtureExternalId, locale),
      });
    },
  });

  if (!editing) {
    return (
      <IconButton
        size="small"
        aria-label={`Rename ${label}`}
        /*
         * Revealed on hover and on focus, like Edit › Fixtures' detach — an
         * edit affordance on every row of a screen whose job is browsing is
         * noise, and one that exists only under the pointer does not exist for
         * the keyboard.
         *
         * Its own attribute rather than `FixtureCard`'s `data-row-affordance`,
         * and the rule that lifts it lives on the market head below. That one
         * is scoped to the whole fixture card, and the drill-down renders
         * inside the card's expand band — so hovering the fixture row anywhere
         * revealed every market's pencil at once. On a World Cup fixture that
         * is 141 of them, which is precisely the noise this is avoiding.
         */
        data-market-affordance
        onClick={(event) => {
          // The head toggles the market open; renaming is not opening.
          event.stopPropagation();
          setDraft(market.displayNameOverride ?? '');
          /*
           * The mutation outlives the form. Without this, a save that failed
           * leaves its error mounted and the next open shows "Could not save."
           * over an untouched field, before anything has been attempted.
           */
          save.reset();
          setEditing(true);
        }}
        sx={(theme: Theme) => ({
          width: '26px',
          height: '26px',
          opacity: 0,
          transition: 'opacity 0.12s',
          borderRadius: `${theme.tokens.radius.idChip}px`,
          color: theme.tokens.color.ink4,
          '&:hover': { backgroundColor: theme.tokens.color.bg, color: theme.tokens.color.ink2 },
        })}
      >
        <EditIcon sx={{ fontSize: '13px' }} />
      </IconButton>
    );
  }

  return (
    <Box
      sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}
      onClick={(event) => event.stopPropagation()}
    >
      <Box sx={{ width: '200px' }}>
        <AdminInput
          label={`Display name for ${label}`}
          hideLabel
          value={draft}
          onChange={setDraft}
          placeholder="Leave blank to use the feed’s name"
          disabled={save.isPending}
        />
      </Box>
      <Button
        variant="contained"
        size="small"
        disabled={save.isPending}
        onClick={() => save.mutate()}
      >
        Save
      </Button>
      <Button
        variant="text"
        size="small"
        disabled={save.isPending}
        onClick={() => setEditing(false)}
      >
        Cancel
      </Button>
      {save.isError ? (
        <Box
          role="alert"
          sx={(theme: Theme) => ({ fontSize: '12px', color: theme.tokens.color.danger })}
        >
          Could not save.
        </Box>
      ) : null}
    </Box>
  );
};

const MarketCard = ({
  market,
  fixtureLabel,
  locale,
  editableMarketName,
}: {
  market: CatalogueMarket;
  fixtureLabel: string;
  locale: CatalogueLocale;
  editableMarketName: boolean;
}) => {
  const [open, setOpen] = useState(false);
  const label = marketLabel(market);

  const outcomes = useQuery({
    queryKey: outcomesQueryKey(market.externalId, locale),
    queryFn: () => catalogueApi.listOutcomesForMarket(market.externalId, locale),
    staleTime: CATALOGUE_STALE_MS,
    // The whole point of the drill-down: a fixture with 141 markets must not
    // fetch 141 lists of outcomes to draw its heads.
    enabled: open,
  });

  const loaded: CatalogueOutcome[] | undefined = outcomes.data;
  /*
   * `active === true`, not `!== false`. `null` is the catalogue declining to
   * say, which `OutcomeStatus` draws as an em-dash rather than as Active — so
   * counting it here would put "4 outcomes · 4 active" over four rows that
   * each say nothing of the kind.
   */
  const activeCount = loaded?.filter((outcome) => outcome.active === true).length;

  return (
    <Box
      sx={(theme: Theme) => ({
        /*
         * Load-bearing. The list above is a capped flex column, and a flex
         * child shrinks to fit by default — 141 cards in 420px squashed every
         * one of them to a 3px hairline, which is what a real World Cup
         * fixture does to this component and what no mock in the handoff
         * could have shown.
         */
        flexShrink: 0,
        backgroundColor: theme.tokens.color.surface,
        border: `${theme.tokens.border.width} solid ${theme.tokens.color.lineStrong}`,
        borderRadius: `${theme.tokens.radius.insetRow}px`,
        overflow: 'hidden',
        ...(open ? { boxShadow: theme.tokens.shadow.card } : {}),
      })}
    >
      <Box
        onClick={() => setOpen((previous) => !previous)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          padding: '10px 14px',
          // This market's own rename pencil, and no sibling's — see the note on
          // `data-market-affordance`. Focus as well as hover, so the keyboard
          // reaches it too.
          '&:hover [data-market-affordance], & [data-market-affordance]:focus-visible': {
            opacity: 1,
          },
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '2px' }}>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '10px', minWidth: 0 }}>
            <Box
              component="span"
              sx={(theme: Theme) => ({
                fontSize: '14px',
                fontWeight: 600,
                letterSpacing: '-0.005em',
                color: theme.tokens.color.ink,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              })}
            >
              {label}
            </Box>
          </Box>
          {/*
           * `.m-mkt-d-meta` reads "N outcomes · N active" in the prototype,
           * whose mock data has every outcome to hand. The real catalogue
           * answers a market at a time, so the line appears when the figures
           * do — the same rule `FixtureRow` follows for its market count, and
           * for the same reason: zero is a reading an operator acts on and must
           * not be shown before it is true.
           */}
          {loaded ? (
            <Box
              sx={(theme: Theme) => ({
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontSize: '12px',
                color: theme.tokens.color.ink3,
                fontVariantNumeric: 'tabular-nums',
              })}
            >
              <span>
                {loaded.length} {loaded.length === 1 ? 'outcome' : 'outcomes'}
              </span>
              <span>·</span>
              <span>{activeCount} active</span>
            </Box>
          ) : null}
        </Box>

        {editableMarketName ? <MarketRename market={market} label={label} locale={locale} /> : null}

        <MarketInfoButton
          market={market}
          label={label}
          fixtureLabel={fixtureLabel}
          outcomeSummary={loaded ? `${loaded.length} total · ${activeCount} active` : undefined}
        />

        <IconButton
          size="small"
          aria-expanded={open}
          aria-label={open ? `Collapse outcomes for ${label}` : `Expand outcomes for ${label}`}
          onClick={(event) => {
            event.stopPropagation();
            setOpen((previous) => !previous);
          }}
          sx={(theme: Theme) => ({
            width: '28px',
            height: '28px',
            borderRadius: `${theme.tokens.radius.control}px`,
            color: theme.tokens.color.ink4,
            '&:hover': { backgroundColor: theme.tokens.color.bg, color: theme.tokens.color.ink2 },
          })}
        >
          {open ? <ChevronDownIcon fontSize="inherit" /> : <ChevronRightIcon fontSize="inherit" />}
        </IconButton>
      </Box>

      {open ? (
        <Box
          sx={(theme: Theme) => ({
            borderTop: theme.tokens.border.default,
            backgroundColor: theme.tokens.color.surfaceMuted,
            // The table's five columns are what it is for; below the breakpoint
            // it scrolls inside itself rather than shedding one, the trade
            // CAE-1542 and CAE-1574 both settled this way.
            overflowX: 'auto',
          })}
        >
          {outcomes.isPending ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', padding: '18px' }}>
              <CircularProgress size={18} />
            </Box>
          ) : outcomes.isError ? (
            <Box
              role="alert"
              sx={(theme: Theme) => ({
                padding: '16px 14px',
                fontSize: '12.5px',
                color: theme.tokens.color.ink3,
              })}
            >
              Could not load outcomes for this market.
            </Box>
          ) : loaded && loaded.length === 0 ? (
            <Box
              sx={(theme: Theme) => ({
                padding: '16px 14px',
                fontSize: '12.5px',
                fontStyle: 'italic',
                color: theme.tokens.color.ink4,
              })}
            >
              The feed holds this market but has sent no outcomes for it.
            </Box>
          ) : (
            <Box sx={{ minWidth: '420px' }}>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: OUTCOME_COLUMNS,
                  gap: '12px',
                  padding: '10px 14px 6px',
                }}
              >
                <ColumnHead>Outcome</ColumnHead>
                <ColumnHead align="right">Decimal</ColumnHead>
                <ColumnHead align="right">Fractional</ColumnHead>
                <ColumnHead>Status</ColumnHead>
                <ColumnHead />
              </Box>
              {(loaded ?? []).map((item) => (
                <Box
                  key={item.externalId}
                  data-testid={`drilldown-outcome-${item.externalId}`}
                  sx={(theme: Theme) => ({
                    display: 'grid',
                    gridTemplateColumns: OUTCOME_COLUMNS,
                    gap: '12px',
                    alignItems: 'center',
                    padding: '10px 14px',
                    borderTop: theme.tokens.border.default,
                    transition: 'background 0.12s',
                    '&:hover': { backgroundColor: theme.tokens.color.surfaceMuted },
                  })}
                >
                  {/*
                   * `.m-outcome-col-name` — the name alone.
                   *
                   * CAE-1555 describes these rows as carrying "an outcome ID
                   * chip", and nothing in the handoff draws one: the catalogue's
                   * outcome row is name, decimal, fractional, status and the
                   * info button, and `id-chip` appears only as a *contest* id in
                   * page headers. The id is in the popover beside it, which is
                   * where `OutcomeInfoPopover` puts it — and a 36-character UUID
                   * under every name would be the loudest thing in the list.
                   * The prototype wins, as it has through the epic; logged in
                   * docs/design/admin-ui/prototype/README.md.
                   */}
                  <Box
                    sx={(theme: Theme) => ({
                      fontSize: '13.5px',
                      fontWeight: 500,
                      letterSpacing: '-0.005em',
                      color: theme.tokens.color.ink,
                      minWidth: 0,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    })}
                  >
                    {item.localisedName ?? item.name}
                  </Box>
                  <Price>{item.decimalPrice === null ? null : item.decimalPrice.toFixed(2)}</Price>
                  <Price>{item.fractionalPrice}</Price>
                  <Box>
                    {/*
                     * `active: null` is the catalogue declining to say, which is
                     * neither on nor off the board — `OutcomeStatus` em-dashes
                     * it rather than guessing.
                     */}
                    <OutcomeStatus
                      suspended={item.active === null ? null : item.active === false}
                    />
                  </Box>
                  <OutcomeInfoButton
                    outcome={item}
                    label={item.localisedName ?? item.name}
                    eyebrow={label}
                    fixtureLabel={fixtureLabel}
                  />
                </Box>
              ))}
            </Box>
          )}
        </Box>
      ) : null}
    </Box>
  );
};

export interface FixtureMarketDrilldownProps {
  fixtureExternalId: string;
  /** What to call the fixture in the market popover — the row's own heading. */
  fixtureLabel: string;
  /**
   * How many markets the caller already knows the feed holds, off whatever
   * payload it is drawing the row from.
   *
   * Zero is answered without a request: the caller has already been told there
   * is nothing to fetch, and asking again to be told the same thing is a round
   * trip to draw an empty state with.
   */
  marketCount: number;
  locale?: CatalogueLocale;
  /*
   * No `onFeedUnreachable`. I added one and took it out again: this sync is
   * always `syncMarkets([fixtureExternalId])` — exactly one fixture — and
   * nothing it can come back with tells a dead feed from a stale event.
   * `fetch_failed` is the backend's catch-all for any per-fixture error that is
   * not `NoActiveResults` (`handleSyncMarketsFixtureError`), so a 404 on one
   * event the feed has dropped looks identical to Caesars being gone; and with
   * one fixture attempted, "nothing got through" is true of both. The result
   * was that expanding a single stale row and syncing it replaced the whole
   * Catalogue — sidebar, toolbar, list and the row being worked in — with the
   * disconnected takeover, while the feed was fine.
   *
   * The pill belongs to the one call with whole-feed scope, `syncFixtures`,
   * which reports every Sport it could not reach; since CAE-1597 the Catalogue
   * reads it from the recorded runs (`feedStatus`). What this one owes its
   * caller is the skip, through `onSyncFailed`.
   */
  /**
   * Offer the in-place rename on each market head — `markets.display_name_override`.
   *
   * The Catalogue alone. It is the only screen in the admin that writes the
   * column, and the two modals that mount this drill-down are pickers: renaming
   * a feed market from inside one would be a second job on a surface whose
   * point is choosing.
   */
  editableMarketName?: boolean;
  /**
   * The feed has been re-pulled for this fixture and delivered something — bust
   * whatever payload the caller took `marketCount` from, and say so.
   */
  onSynced?: (message: string) => void;
  /** The sync failed, or resolved having skipped this fixture. */
  onSyncFailed?: (message: string) => void;
}

export const FixtureMarketDrilldown = ({
  fixtureExternalId,
  fixtureLabel,
  marketCount,
  locale = 'en',
  editableMarketName = false,
  onSynced,
  onSyncFailed,
}: FixtureMarketDrilldownProps) => {
  const queryClient = useQueryClient();

  /*
   * A sync this component fired has delivered markets for a fixture the
   * caller's payload still calls empty.
   *
   * `marketCount` is a prop, and the caller is under no obligation to re-read
   * the payload it came from — `onSynced` is optional. Without this the panel
   * would sit on "No markets synced" after a sync that plainly worked, with
   * its own query disabled and nothing on screen able to re-enable it. The
   * component answers for its own state; telling the caller stays a courtesy.
   */
  const [syncDelivered, setSyncDelivered] = useState(false);

  /*
   * The sync lives here rather than at the call site because it is the same
   * action on all three screens that mount this, and because the query it
   * invalidates is this component's own. What it cannot know is what *else*
   * went stale — Edit › Fixtures reads its market counts off the contest
   * payload, which the sync has just changed — so the caller is told, and busts
   * its own.
   */
  const sync = useMutation({
    mutationFn: () => catalogueApi.syncMarkets([fixtureExternalId]),
    onSuccess: async (result) => {
      /*
       * Outcomes as well as markets.
       *
       * A sync re-pulls both, and a market that comes back under the same
       * `externalId` keeps its outcomes cache key — so busting the market list
       * alone leaves an already-expanded market redrawing yesterday's prices
       * and `active` flags. The whole catalogue prefix, which is what
       * `CataloguePage`'s own sync invalidates: these queries are lazy, so the
       * ones nobody is looking at cost nothing to mark stale.
       */
      await queryClient.invalidateQueries({ queryKey: ['admin', 'catalogue'] });

      /*
       * A resolved call is not a successful sync. The feed can skip the
       * fixture — it no longer holds it, or it returned nothing active — and
       * the response says so in `skipped` while still resolving. Reported as
       * a failure, because from where the operator is standing it is one:
       * they pressed the button and the panel still says there are no
       * markets.
       */
      if (syncDeliveredNothing(result)) {
        onSyncFailed?.(
          `No markets synced for this fixture — ${summariseSkippedReasons(result.skipped)}.`,
        );
        return;
      }

      setSyncDelivered(true);
      const removed =
        result.removed > 0
          ? ` Removed ${result.removed} stale ${result.removed === 1 ? 'market' : 'markets'}.`
          : '';
      onSynced?.(
        `Synced ${result.markets} ${result.markets === 1 ? 'market' : 'markets'} and ${
          result.outcomes
        } ${result.outcomes === 1 ? 'outcome' : 'outcomes'} for this fixture.${removed}`,
      );
    },
    onError: (error) =>
      onSyncFailed?.(error instanceof Error ? error.message : 'Failed to sync markets.'),
  });

  /*
   * Whether there is anything to fetch — the caller's count, or a sync of our
   * own that has since said otherwise. Still false for a known-empty fixture
   * nobody has synced, which is what keeps that case free of requests.
   */
  const expectsMarkets = marketCount > 0 || syncDelivered;

  const markets = useQuery({
    queryKey: marketsQueryKey(fixtureExternalId, locale),
    queryFn: () => catalogueApi.listMarketsForFixture(fixtureExternalId, locale),
    staleTime: CATALOGUE_STALE_MS,
    enabled: expectsMarkets,
  });

  /*
   * Zero from either source.
   *
   * `marketCount` is the caller's payload and is what lets a known-empty
   * fixture open without a request at all. It can also be stale — the
   * contest detail was fetched before a purge or a re-sync — and falling
   * through on a fetched empty list rendered a blank box with no message and
   * no way to sync out of it.
   */
  const noMarkets = !expectsMarkets || (markets.isSuccess && markets.data.length === 0);

  if (noMarkets) {
    return (
      <AdminEmptySection
        tone="warn"
        icon={<RefreshIcon />}
        title="No markets synced for this fixture"
        subtitle="Players can't pick outcomes from this fixture until markets are synced from the feed."
        action={{
          label: sync.isPending ? 'Syncing…' : 'Sync markets',
          icon: <RefreshIcon />,
          onClick: () => {
            if (!sync.isPending) sync.mutate();
          },
        }}
      />
    );
  }

  if (markets.isPending) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', padding: '24px' }}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  if (markets.isError) {
    return (
      <Box
        role="alert"
        sx={(theme: Theme) => ({ fontSize: '12.5px', color: theme.tokens.color.ink3 })}
      >
        Could not load markets for this fixture.
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        maxHeight: `${MARKET_LIST_MAX_HEIGHT}px`,
        overflowY: 'auto',
        // The cards' focus rings and hover borders would otherwise be clipped
        // flush against the scroll container's edge.
        padding: '2px',
        margin: '-2px',
      }}
    >
      {(markets.data ?? []).map((market) => (
        <MarketCard
          key={market.externalId}
          market={market}
          fixtureLabel={fixtureLabel}
          locale={locale}
          editableMarketName={editableMarketName}
        />
      ))}
    </Box>
  );
};
